/**
 * Requires an explicit variant selection on the product page:
 * - No option shows as pre-selected on load (the "needs-selection" class in
 *   bluorng-collection.css hides the selected-swatch pill/colors).
 * - The Add to Cart button looks and stays normal, but the moment the
 *   shopper tries to activate it (mouse, touch, or keyboard) before picking
 *   an option, it's disabled just-in-time - before the click/submit even
 *   fires - and an inline error asks them to select one instead. Because
 *   this happens at the native "is this button disabled" check, it also
 *   blocks the theme's own optimistic add-to-cart animation, not just the
 *   network request.
 * - "Buy it now" (Shopify's accelerated checkout web component) can't be
 *   intercepted this way - it's a cross-origin component - so it's hidden
 *   entirely until a selection is made.
 * - The moment the shopper picks an option, everything reverts to normal
 *   theme behavior (including its own availability/sold-out handling).
 *
 * Implementation notes:
 * - Disabling the button on pointerdown/keydown (rather than trying to
 *   preventDefault the later click/submit events) is what makes this
 *   reliable: a disabled button never dispatches a click event at all, so
 *   we don't have to race the theme's own event listeners or guess their
 *   registration order.
 * - Reuses the theme's own error-message slot (`[ref="addToCartTextError"]`)
 *   so the message looks and behaves like the theme's native errors.
 * - Only targets the variant picker that matches the current product page
 *   (`data-template-product-match="true"`), so collection cards, quick add,
 *   and recommendations are untouched.
 */
(function () {
  const DEFAULT_MESSAGE = 'Please select an option before adding to cart.';
  const REENABLE_DELAY = 60;

  /** @param {Element} fieldset */
  function fieldsetLabel(fieldset) {
    const legend = fieldset.querySelector('legend');
    if (!legend) return '';
    // The legend can contain a nested swatch-value span; only take the option name text.
    return (legend.childNodes[0]?.textContent || legend.textContent || '').trim();
  }

  /** @param {Element} picker */
  function buildMessage(picker) {
    const names = Array.from(picker.querySelectorAll('fieldset')).map(fieldsetLabel).filter(Boolean);
    if (!names.length) return DEFAULT_MESSAGE;
    if (names.length === 1) return `Please select a ${names[0]} before adding to cart.`;
    const last = names[names.length - 1];
    const rest = names.slice(0, -1).join(', ');
    return `Please select ${rest} and ${last} before adding to cart.`;
  }

  /** @param {string} productId */
  function findPicker(productId) {
    return document.querySelector(
      `variant-picker[data-template-product-match="true"][data-product-id="${CSS.escape(productId)}"]`
    );
  }

  /**
   * @param {Element} productForm
   * @param {string} message
   */
  function showError(productForm, message) {
    const errorEl = productForm.querySelector('[ref="addToCartTextError"]');
    if (!errorEl) {
      window.alert(message);
      return;
    }

    errorEl.classList.remove('hidden');
    const textNode = Array.from(errorEl.childNodes).find((node) => node.nodeType === Node.TEXT_NODE);
    if (textNode) {
      textNode.textContent = message;
    } else {
      errorEl.appendChild(document.createTextNode(message));
    }

    window.clearTimeout(errorEl.__requireVariantTimeout);
    errorEl.__requireVariantTimeout = window.setTimeout(() => {
      errorEl.classList.add('hidden');
    }, 4000);
  }

  /** @param {Element} picker */
  function markInitialState(picker) {
    if (/** @type {HTMLElement} */ (picker).dataset.requireSelectionInit === 'true') return;

    const fieldsets = picker.querySelectorAll('fieldset');
    if (!fieldsets.length) return; // Single-variant product - nothing to require.

    /** @type {HTMLElement} */ (picker).dataset.requireSelectionInit = 'true';
    picker.classList.add('needs-selection');

    // The theme pre-checks the first available combination's radios. If we
    // only hid that visually, clicking that *same* already-checked option
    // (e.g. the first size) would fire no 'change' event at all - browsers
    // don't dispatch one when a radio's checked state doesn't actually
    // change - so nothing would ever unlock. Actually uncheck them so every
    // option, including the one that started pre-checked, is a real state
    // change. variant-picker.js reads the static `data-current-checked`
    // attribute (not the live `checked` property) for its own initial
    // state, and cart submission reads a separate JS-managed hidden input,
    // so this doesn't disturb either.
    picker.querySelectorAll('fieldset input:checked').forEach((input) => {
      /** @type {HTMLInputElement} */ (input).checked = false;
    });

    const productId = picker.getAttribute('data-product-id');
    const productForm =
      productId && document.querySelector(`product-form-component[data-product-id="${CSS.escape(productId)}"]`);
    productForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]')?.setAttribute('hidden', 'true');

    picker.addEventListener(
      'change',
      () => {
        picker.classList.remove('needs-selection');
        productForm?.querySelector('[ref="acceleratedCheckoutButtonContainer"]')?.removeAttribute('hidden');
      },
      { once: true }
    );
  }

  /** @param {HTMLButtonElement} button */
  function guardButton(button) {
    const addToCartComponent = button.closest('add-to-cart-component');
    const productForm = addToCartComponent?.closest('product-form-component');
    const productId = productForm?.getAttribute('data-product-id');
    if (!productForm || !productId) return;

    const picker = findPicker(productId);
    if (!picker) return;

    markInitialState(picker);
    if (!picker.classList.contains('needs-selection')) return;

    // Disable right now, before the click/keypress activation fires, so the
    // browser never dispatches a click event for this attempt at all.
    button.disabled = true;

    showError(productForm, buildMessage(picker));
    picker.classList.add('shake-attention');
    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });

    window.setTimeout(() => {
      button.disabled = false;
      picker.classList.remove('shake-attention');
    }, REENABLE_DELAY);
  }

  /** @param {Event} event */
  function handlePointerDownCapture(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = /** @type {HTMLButtonElement | null} */ (target.closest('add-to-cart-component [ref="addToCartButton"]'));
    if (!button || button.disabled) return;
    guardButton(button);
  }

  /** @param {KeyboardEvent} event */
  function handleKeydownCapture(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = /** @type {HTMLButtonElement | null} */ (target.closest('add-to-cart-component [ref="addToCartButton"]'));
    if (!button || button.disabled) return;
    guardButton(button);
  }

  function scanPickers() {
    document.querySelectorAll('variant-picker[data-template-product-match="true"]').forEach(markInitialState);
  }

  // Capture phase, and on pointerdown/keydown rather than click - see notes above.
  document.addEventListener('pointerdown', handlePointerDownCapture, true);
  document.addEventListener('keydown', handleKeydownCapture, true);
  document.addEventListener('DOMContentLoaded', scanPickers);
  document.addEventListener('shopify:section:load', scanPickers);
  scanPickers();
})();
